/**
 * Driftsbesked-popup: viser den nyeste aktive system_alert som dialog.
 * Afvisning gemmes pr. alert (id + updated_at) i localStorage, så en
 * opdateret besked vises igen.
 */
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Info, OctagonAlert } from "lucide-react";
import { supabase } from "@/lib/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const STORAGE_KEY = "lab3d_dismissed_system_alerts";

function dismissedKeys(): string[] {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "[]") as string[];
  } catch {
    return [];
  }
}

function alertKey(id: string, updatedAt: string): string {
  return `${id}_${updatedAt}`;
}

export function SystemAlertPopup() {
  const [open, setOpen] = useState(false);

  const { data: alert } = useQuery({
    queryKey: ["systembesked"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("system_alerts")
        .select("id, title, message, alert_type, updated_at")
        .eq("is_active", true)
        .order("updated_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (alert && !dismissedKeys().includes(alertKey(alert.id, alert.updated_at))) {
      setOpen(true);
    }
  }, [alert]);

  if (!alert) return null;

  const dismiss = () => {
    const keys = dismissedKeys();
    const key = alertKey(alert.id, alert.updated_at);
    if (!keys.includes(key)) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...keys, key]));
    }
    setOpen(false);
  };

  const Icon =
    alert.alert_type === "error"
      ? OctagonAlert
      : alert.alert_type === "info"
        ? Info
        : AlertTriangle;
  const iconClass =
    alert.alert_type === "error"
      ? "text-destructive"
      : alert.alert_type === "info"
        ? "text-primary"
        : "text-amber-500";

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : dismiss())}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className={`h-5 w-5 ${iconClass}`} aria-hidden />
            {alert.title}
          </DialogTitle>
          <DialogDescription className="whitespace-pre-wrap text-left">
            {alert.message}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button className="min-h-11 w-full sm:w-auto" onClick={dismiss}>
            OK, forstået
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
